/**
 * Payroll Report Page
 *
 * Shows payroll summary by crew member for a date range.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getPayrollReport, downloadReportCSV, PayrollCrewData } from '../../../api/reports';
import { getErrorMessage } from '../../../api/client';
import { ReportFilters, ReportSummaryCard, useReportDateRange } from '../../../components/reports';

export default function PayrollReportPage() {
  const [dateRange, setDateRange] = useReportDateRange();
  const [report, setReport] = useState<PayrollCrewData[]>([]);
  const [totals, setTotals] = useState({ totalHours: 0, totalPay: 0, totalAssignments: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getPayrollReport({
        startDate: new Date(dateRange.startDate).toISOString(),
        endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
      });
      setReport(response.report);
      setTotals(response.totals);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Export to CSV
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await downloadReportCSV(
        '/reports/payroll',
        {
          startDate: new Date(dateRange.startDate).toISOString(),
          endDate: new Date(dateRange.endDate + 'T23:59:59').toISOString(),
        },
        'payroll-report.csv'
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);
  };

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="mb-4">
        <Link to="/admin/reports" className="text-primary-600 hover:text-primary-700 text-sm">
          &larr; Back to Reports
        </Link>
      </nav>

      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Report</h1>
        <p className="text-gray-600 mt-1">
          Crew payment totals by member for the selected date range
        </p>
      </div>

      {/* Filters */}
      <ReportFilters
        dateRange={dateRange}
        onChange={setDateRange}
        onExportCSV={handleExport}
        isExporting={isExporting}
      />

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Summary cards */}
      {!loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <ReportSummaryCard
            label="Total Pay"
            value={formatCurrency(totals.totalPay)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <ReportSummaryCard
            label="Total Hours"
            value={totals.totalHours.toFixed(1)}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
          <ReportSummaryCard
            label="Total Assignments"
            value={totals.totalAssignments.toString()}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            }
          />
        </div>
      )}

      {/* Report table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-rack border-r-transparent" />
            <p className="mt-2 text-gray-600">Loading report...</p>
          </div>
        ) : report.length === 0 ? (
          <div className="p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No data found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No payroll data for the selected date range
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Crew Member
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assignments
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Events
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Pay
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {report.map((row) => (
                  <tr key={row.userId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <Link
                          to={`/admin/reports/crew/${row.userId}/history?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`}
                          className="font-medium text-gray-900 hover:text-primary-600"
                        >
                          {row.firstName} {row.lastName}
                        </Link>
                        <p className="text-sm text-gray-500">{row.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {row.totalHours.toFixed(1)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {row.assignmentCount}
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-900">
                      {row.eventCount}
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                      {formatCurrency(row.totalPay)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">
                    Total
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {totals.totalHours.toFixed(1)}
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    {totals.totalAssignments}
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-medium text-gray-900">
                    -
                  </td>
                  <td className="px-6 py-3 text-right text-sm font-bold text-gray-900">
                    {formatCurrency(totals.totalPay)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
